import { Button, ButtonSize, ButtonVariant, isMobile, Swipeable, SwipeDirection } from '@farmasiet/frontendcore-reactcomponents';
import * as React from 'react';
import { useEffect, useRef } from 'react';

import { LocalStorageAccessor } from '../../StorageAccessor/LocalStorageAccessor';
import { Modal, ModalButtons, ModalHeading } from './Modal';

export interface SessionNotificationModalProps {
    className: string;
    expirationDate: number | null;
    dismissKey: string;
    onClose?: (fromButton: boolean) => void;
    onConfirm?: (isEcomLogout: boolean) => void;
    heading?: string;
    additionalContent?: JSX.Element;
    confirmButton?: JSX.Element;
    confirmLabel?: string;
    cancelLabel?: string;
    minNotificationThresholdMs?: number;
    maxNotificationThresholdMs: number;
    sessionEndedEvent?: string;
    ecomLogoutEvent?: string;
    validateBeforeShowing?: (isEcomLogout?: boolean) => Promise<boolean>;
    image?: string;
    showDragHandle?: boolean;
}

export const SessionNotificationModal = ({
    className,
    expirationDate,
    dismissKey,
    onConfirm,
    confirmButton,
    confirmLabel,
    cancelLabel,
    onClose,
    heading,
    additionalContent,
    minNotificationThresholdMs,
    maxNotificationThresholdMs,
    sessionEndedEvent,
    ecomLogoutEvent,
    validateBeforeShowing,
    showDragHandle
}: SessionNotificationModalProps) => {
    const [isVisible, setIsVisible] = React.useState(false);
    const [isEcomLogout, setIsEcomLogout] = React.useState(false);

    const expiringMaxTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const expiringMinTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const ExpirationDelay = 1000;

    useEffect(() => {
        if (sessionEndedEvent) {
            window.addEventListener(sessionEndedEvent, validateAndShowSessionModal);
        }

        if (ecomLogoutEvent) {
            window.addEventListener(ecomLogoutEvent, validateAndShowEcomLogoutModal);
        }

        return () => {
            if (sessionEndedEvent) {
                window.removeEventListener(sessionEndedEvent, validateAndShowSessionModal);
            }

            if (ecomLogoutEvent) {
                window.removeEventListener(ecomLogoutEvent, validateAndShowEcomLogoutModal);
            }

            expiringMaxTimeout.current && clearTimeout(expiringMaxTimeout.current);
            expiringMinTimeout.current && clearTimeout(expiringMinTimeout.current);
        };
    }, []);

    useEffect(() => {
        if (expirationDate) {
            const expiresIn = expirationDate - getNowUtc().getTime() + ExpirationDelay;

            if (isNotDismissed()) {
                setupNotification(expiresIn);
            }
        }
    }, [expirationDate]);

    const getNowUtc = () => {
        const date = new Date();
        const utc = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
        return new Date(utc);
    };

    const setupNotification = (expiresIn: number) => {
        expiringMaxTimeout.current && clearTimeout(expiringMaxTimeout.current);
        expiringMinTimeout.current && clearTimeout(expiringMinTimeout.current);

        if (minNotificationThresholdMs && expiresIn <= minNotificationThresholdMs) {
            return;
        }

        if (expiresIn <= maxNotificationThresholdMs) {
            validateAndShowSessionModal();
        } else {
            scheduleModalShowingAfterMaxNotificationThreshold(expiresIn);
        }

        scheduleModalHidingAfterMinNotificationThreshold(expiresIn);
    };

    const scheduleModalHidingAfterMinNotificationThreshold = (expiresIn: number) => {
        if (minNotificationThresholdMs) {
            expiringMinTimeout.current = setTimeout(() => {
                setIsVisible(false);
            }, expiresIn - minNotificationThresholdMs);
        }
    };

    const validateAndShowModal = (isEcomLogout: boolean) => {
        setIsEcomLogout(isEcomLogout);
        if (validateBeforeShowing) {
            const validationResult = validateBeforeShowing(isEcomLogout);
            validationResult.then((result) => {
                if (result) {
                    setIsVisible(true);
                }
            });
        } else {
            setIsVisible(true);
        }
    };

    const validateAndShowEcomLogoutModal = () => validateAndShowModal(true);
    const validateAndShowSessionModal = () => validateAndShowModal(false);

    const scheduleModalShowingAfterMaxNotificationThreshold = (expiresIn: number) => {
        expiringMaxTimeout.current = setTimeout(() => {
            if (isNotDismissed()) {
                validateAndShowSessionModal();
            }
        }, expiresIn - maxNotificationThresholdMs);
    };

    const isNotDismissed = () => {
        const dismissDate = LocalStorageAccessor.getItem(dismissKey);
        if (!dismissDate) {
            return true;
        }

        return Number.parseInt(dismissDate) !== expirationDate;
    };

    const handleSwipe = (swipeDirection: SwipeDirection) => {
        if (isMobile() && swipeDirection === SwipeDirection.Bottom) {
            setIsVisible(false);
        }
    };

    const handleClose = (fromButton: boolean) => {
        expirationDate && LocalStorageAccessor.setItem(dismissKey, expirationDate.toString());
        setIsVisible(false);
        onClose && onClose(fromButton);
    };

    const handleConfirm = () => {
        !isEcomLogout && expirationDate && LocalStorageAccessor.setItem(dismissKey, expirationDate.toString());
        onConfirm && onConfirm(isEcomLogout);
    };

    return (
        <Swipeable onSwipe={handleSwipe}>
            <Modal open={isVisible} onClose={() => handleClose(false)} className={className}>
                {showDragHandle && <div className="PhilModal__DragHandle" />}
                <ModalHeading>{heading}</ModalHeading>
                {additionalContent}
                <ModalButtons>
                    <Button size={ButtonSize.Medium} variant={ButtonVariant.Link} onClick={() => handleClose(true)}>
                        {cancelLabel}
                    </Button>
                    <Button size={ButtonSize.Medium} variant={ButtonVariant.Primary} onClick={handleConfirm}>
                        {confirmButton || confirmLabel}
                    </Button>
                </ModalButtons>
            </Modal>
        </Swipeable>
    );
};
